export const seoPages = [
  {
    slug: 'stripe',
    path: '/stripe',
    title: 'Tillio POS + Stripe',
    seoTitle: 'Restaurant POS Powered by Stripe | Tillio POS',
    seoDescription:
      'Tillio POS is restaurant point of sale powered by Stripe. Connect the Stripe account you already have, pair a Stripe Terminal reader, and take chip, tap, and swipe at the table or counter.',
    images: {
      hero: '/feature-payments.webp',
      section1: '/showcase-payments.webp', // What Tillio is—and what Stripe does
      section2: '/category-take-payments.webp', // Connect with Stripe Connect
      section3: '/hardware-terminal.webp', // How a payment works
      section4: '/hardware-terminal.webp', // Stripe Terminal readers
      section5: '/feature-payments.webp', // Your restaurant is the merchant of record
      section6: '/showcase-payments.webp', // Pricing and security
    },
    eyebrow: 'Payments',
    intro:
      'Tillio POS is restaurant point of sale powered by Stripe. Connect the Stripe account you already have, pair a Stripe Terminal reader, and take chip, tap, and swipe at the table or counter. Payouts and disputes stay in Stripe.',
    sections: [
      {
        title: 'What Tillio is—and what Stripe does',
        description:
          'Tillio is restaurant POS software: orders, tables, kitchen display, team, and analytics. Stripe is the payment processor. Cards, payouts, disputes, and PCI-ready card handling stay with Stripe. Tillio does not hold card funds.',
        bullets: [
          'Tillio runs the floor: tickets, tables, tips, and receipts',
          'Stripe processes cards and pays out to your bank',
          'Your restaurant is the business guests pay',
          'Connect the Stripe account you already verified',
        ],
      },
      {
        title: 'Connect with Stripe Connect',
        description:
          'An owner or admin authorizes Tillio from settings using Stripe Connect. You sign in to Stripe—Tillio never sees your Stripe password or asks you to paste API keys. You can revoke access later from the Stripe Dashboard.',
        bullets: [
          'Official Stripe Connect OAuth—no API keys to copy',
          'Keep the Stripe account you already use',
          'Create a Stripe account during connect if you don’t have one',
          'Revoke Tillio access anytime in Stripe',
        ],
      },
      {
        title: 'How a payment works',
        description:
          'Staff close the check in Tillio. The ticket total is sent to a Stripe Terminal reader. The guest taps, inserts, or swipes. The charge settles to your Stripe balance, then Stripe pays out to your bank on Stripe’s schedule.',
        bullets: [
          'Amounts come from the open ticket—no retyping on a keypad',
          'Chip, swipe, and contactless on Stripe Terminal',
          'Tips and split checks stay tied to the order',
          'Refunds and disputes are handled in Stripe',
        ],
      },
      {
        title: 'Stripe Terminal readers',
        description:
          'Pair a Stripe Terminal reader with Tillio for counter or tableside checkout. Choose a countertop smart reader or a portable Bluetooth model. Hardware is optional and purchased as needed.',
        bullets: [
          'Stripe Reader S700 and S710',
          'Stripe Reader M2',
          'BBPOS WisePOS E and WisePad 3',
          'Customer-facing tipping on supported smart readers',
        ],
      },
      {
        title: 'Your restaurant is the merchant of record',
        description:
          'Guests pay your restaurant, not Tillio. Your business name appears on the card statement. You own the customer relationship for that charge, including refunds and disputes in the Stripe Dashboard you already use.',
        bullets: [
          'Restaurant name on the guest’s statement',
          'Funds settle to your Stripe balance',
          'Payouts go to your bank on Stripe’s schedule',
          'Dispute and refund tools stay in Stripe',
        ],
      },
      {
        title: 'Pricing and security',
        description:
          'Platform access is $99/mo with unlimited devices for your location. Card payments include a 1.1% Tillio flat rate plus Stripe’s own processing rates. Card data is handled by Stripe—Tillio does not store card numbers.',
        bullets: [
          '$99/mo platform fee, unlimited devices',
          '1.1% Tillio flat rate + Stripe processing fees',
          'PCI-ready card handling via Stripe',
          'Privacy and terms at tilliopayment.com — support: services@tilliopayment.com',
        ],
      },
    ],
    relatedFeatureSlugs: ['payments', 'point-of-sale', 'analytics'],
    ctaTitle: 'Connect Stripe and start taking payments',
    ctaDescription:
      'Authorize the Stripe account you already have, pair a Terminal reader, and close checks in Tillio.',
    ctaButtonLabel: 'Get started',
    resourceLinks: [
      { path: '/guides/connect-stripe-account', label: 'Connect Stripe guide' },
      { path: '/card-readers', label: 'Card readers' },
      { path: '/pricing', label: 'Pricing' },
      { path: '/how-it-works', label: 'How it works' },
      { path: '/privacy', label: 'Privacy policy' },
      { path: '/terms', label: 'Terms of service' },
    ],
  },
  {
    slug: 'how-it-works',
    path: '/how-it-works',
    title: 'How Tillio Works',
    seoTitle: 'How It Works: Connect Stripe & Run Restaurant POS | Tillio',
    seoDescription:
      'Connect your existing Stripe account to Tillio POS, pair Stripe Terminal readers, and start taking in-person payments—orders, tables, and checkout in one system.',
    images: {
      hero: '/feature-payments.webp',
      section1: '/feature-pos.webp', // 1. Create your Tillio account
      section2: '/showcase-payments.webp', // 2. Connect your Stripe account
      section3: '/hardware-terminal.webp', // 3. Accept payments on the floor
    },
    eyebrow: 'Getting started',
    intro:
      'Tillio is restaurant POS software that connects to the Stripe account you already have. Sign up, authorize Stripe, pair a Terminal reader, and take payments tied to real tickets—not a separate payment app.',
    sections: [
      {
        title: '1. Create your Tillio account',
        description:
          'Tell us about your restaurant and get access to the cloud POS. Platform pricing starts at $99/mo with unlimited devices for your location—no setup fee.',
        bullets: [
          'Cloud POS for iPad and compatible tablets',
          'Menus, tables, team roles, and analytics included',
          'No per-device software fees',
          'Onboarding that matches how your floor actually runs',
        ],
      },
      {
        title: '2. Connect your Stripe account',
        description:
          'Authorize Tillio with secure Stripe Connect OAuth from your admin settings. You keep your Stripe account, payouts, and Dashboard—Tillio never holds your card funds.',
        bullets: [
          'Use the Stripe account you already verified',
          'OAuth—no API keys to copy into spreadsheets',
          'Card data stays with Stripe for PCI-ready processing',
          'Same Stripe payouts and dispute tools you know',
        ],
      },
      {
        title: '3. Accept payments on the floor',
        description:
          'Pair a Stripe Terminal reader, ring orders in Tillio, and close checks with chip, swipe, or tap. Totals flow from the ticket to the reader so staff don’t re-enter amounts.',
        bullets: [
          'Stripe Terminal: S700, M2, WisePOS E, WisePad 3, and more',
          '1.1% Tillio flat rate + Stripe processing fees',
          'Split checks, tips, and receipts built into checkout',
          'Payments stay tied to the order and table',
        ],
      },
    ],
    relatedFeatureSlugs: ['payments', 'point-of-sale', 'table-management'],
    resourceLinks: [
      { path: '/stripe', label: 'Tillio + Stripe' },
      { path: '/guides/connect-stripe-account', label: 'Connect Stripe guide' },
      { path: '/card-readers', label: 'Card readers' },
      { path: '/compare/stripe', label: 'Tillio vs Stripe Dashboard' },
      { path: '/pricing', label: 'Pricing' },
    ],
  },
  {
    slug: 'payment-methods',
    path: '/payment-methods',
    title: 'Payment Methods',
    seoTitle: 'Restaurant Payment Methods with Stripe Terminal | Tillio POS',
    seoDescription:
      'Accept chip, swipe, and contactless card payments in your restaurant with Tillio POS and Stripe Terminal—connected to your existing Stripe account.',
    images: {
      hero: '/showcase-payments.webp',
      section1: '/hardware-terminal.webp', // Chip, swipe, and tap
      section2: '/feature-payments.webp', // Checkout that matches the ticket
      section3: '/category-take-payments.webp', // Your Stripe account, your money
    },
    eyebrow: 'Payments',
    intro:
      'Guests expect fast, familiar card payments at the table or counter. Tillio processes in-person cards through Stripe Terminal on the Stripe account you connect—so checkout lives inside the same POS that runs your tickets.',
    sections: [
      {
        title: 'Chip, swipe, and tap',
        description:
          'Stripe Terminal readers support the card entry methods diners already use. Contactless tap, chip insert, and magnetic swipe cover busy lunch lines and tableside closeouts.',
        bullets: [
          'Contactless cards and mobile wallets via Terminal',
          'EMV chip for in-person security',
          'Swipe when needed for older cards',
          'Customer-facing tipping on supported smart readers',
        ],
      },
      {
        title: 'Checkout that matches the ticket',
        description:
          'Amounts come from the open check—not a blank keypad. Servers close tables accurately, split bills when needed, and keep the kitchen and floor in sync while payment completes.',
        bullets: [
          'Totals pulled from the POS order',
          'Split checks and tip workflows',
          'Printed or digital receipts',
          'Dispute context stays with the Stripe charge',
        ],
      },
      {
        title: 'Your Stripe account, your money',
        description:
          'Connect the Stripe account you already run. Stripe charges its own processing rates; Tillio adds a flat 1.1% on cards plus the restaurant POS layer. See stripe.com/pricing for Stripe’s current fees.',
        bullets: [
          'Connect via Stripe Connect OAuth',
          'Funds settle to your Stripe balance and bank',
          'No separate merchant account to migrate into Tillio',
          'See How it works for the full setup path',
        ],
      },
    ],
    relatedFeatureSlugs: ['payments', 'point-of-sale', 'analytics'],
    resourceLinks: [
      { path: '/stripe', label: 'Tillio + Stripe' },
      { path: '/how-it-works', label: 'How it works' },
      { path: '/card-readers', label: 'Card readers' },
      { path: '/features/payments', label: 'Payments feature' },
      { path: '/guides/connect-stripe-account', label: 'Connect Stripe guide' },
    ],
  },
]

export function getSeoPage(slug) {
  return seoPages.find((p) => p.slug === slug)
}
